import React, { Fragment } from 'react';

import { useTranslation } from 'react-i18next';
import { Theme, makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import cx from 'clsx';
import BreakableTypography from '../BreakableTypography';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    borderTop: '8px solid #417c78',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 1px 8px 4px rgba(0, 0, 0, 0.20)',
      transform: 'scale(1.01, 1.01)'
    }
  },
  cardContent: {
    padding: 0,
    paddingTop: theme.spacing(1) / 2,
    color: '#4A4A4A',
    '&:last-child': {
      paddingBottom: 0
    }
  },
  expandedCardContent: {
    paddingTop: theme.spacing(2),
    padding: 0,
    '&:last-child': {
      paddingBottom: 0
    }
  },
  cardHeader: {
    color: '#4A4A4A',
    padding: 0
  },
  cardHeaderAction: {
    marginRight: -8,
    marginTop: 0
  },
  button: {
    borderRadius: 16,
    padding: `${theme.spacing(1) / 4}px ${theme.spacing(1) / 4}px`
  },
  buttonIcon: {
    color: '#4A4A4A'
  },
  downIcon: {
    fontSize: '24px',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest
    })
  },
  downIconRotate: {
    transform: 'rotate(180deg)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest
    })
  }
}));

export interface InformationCardProps {
  expanded: boolean;
  openCard: (index: number) => void;
  closeCard: () => void;
  id: number;
  index: number;
  cardTitle: string;
  Contents: React.ReactNode;
  onClick?: (item: any, index: number) => void;
  borderColor?: string;
  // The typing of the ListItem component in Material is very complicated and currently mismatches its implementation
  actionOptions?: Array<any & { label: string }>;
}

const InformationCard: React.FC<InformationCardProps> = ({
  index,
  onClick: onCardClick,
  id,
  cardTitle,
  actionOptions,
  Contents,
  borderColor = '#417C78'
}) => {
  const classes = useStyles();
  const { t } = useTranslation('StudentCard');

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMoreOptionsMenuClick: React.ReactEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMoreOptionsMenuClose = (): void => {
    setAnchorEl(null);
  };
  const onClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (onCardClick && !anchorEl) {
      onCardClick({ id }, index);
    }
  };

  return (
    <Card
      style={{ borderTopColor: borderColor }}
      id={String(id)}
      onClick={onClick}
      className={onCardClick ? cx(classes.card, classes.clickable) : classes.card}
      data-testid={`${cardTitle} Card`}
    >
      <CardHeader
        disableTypography={true}
        title={<BreakableTypography variant="h5">{cardTitle}</BreakableTypography>}
        classes={{
          root: classes.cardHeader,
          action: classes.cardHeaderAction
        }}
        action={
          <Fragment>
            <IconButton
              className={classes.button}
              id={String(id)}
              aria-label={t('{{cardTitle}} Options', { cardTitle })}
              aria-controls="more-options-menu"
              aria-haspopup="true"
              onClick={handleMoreOptionsMenuClick}
            >
              <MoreVertIcon className={classes.buttonIcon} />
            </IconButton>
            {actionOptions && (
              <Menu
                id="more-options-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={handleMoreOptionsMenuClose}
              >
                {actionOptions.map(({ label, MenuItemProps = {} }) => {
                  const { onClick: actionOnClick, ...restMenuItemProps } = MenuItemProps;
                  const handleClick = (event: React.MouseEvent<HTMLLIElement>): void => {
                    event.stopPropagation();
                    if (actionOnClick) {
                      actionOnClick(event);
                    }
                    handleMoreOptionsMenuClose();
                  };
                  return (
                    <MenuItem
                      key={label}
                      id={anchorEl?.id}
                      onClick={handleClick}
                      {...restMenuItemProps}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </Menu>
            )}
          </Fragment>
        }
      />
      <CardContent className={classes.cardContent}>{Contents}</CardContent>
    </Card>
  );
};

export default InformationCard;
